import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import Header from '../components/Header';
import Footer from '../components/Footer';

// 인증 서버가 거절한 이유를 그대로 알려준다.
// "다시 시도해주세요"만 보여주면 같은 비밀번호를 계속 입력하며 반복 실패한다.
function describeUpdateError(err: any): string {
  const code = err?.code || err?.error_code || '';
  const message = String(err?.message || '');

  if (code === 'same_password' || /should be different from the old password/i.test(message)) {
    return '기존 비밀번호와 다른 비밀번호를 입력해주세요.';
  }
  if (code === 'weak_password' || /password.*(least|short)/i.test(message)) {
    return '비밀번호가 너무 단순합니다. 6자 이상으로, 영문과 숫자를 섞어 입력해주세요.';
  }
  if (code === 'session_not_found' || err?.status === 401 || err?.status === 403) {
    return '재설정 링크가 만료되었습니다. 비밀번호 찾기를 다시 진행해주세요.';
  }

  return '비밀번호 변경에 실패했습니다. 다시 시도해주세요.';
}

const ResetPassword: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isValidSession, setIsValidSession] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [linkError, setLinkError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // 인증 서버가 링크 검증에 실패하면 해시에 사유를 담아 이 페이지로 돌려보낸다.
    const hashError = new URLSearchParams(window.location.hash.substring(1)).get('error_code');
    if (hashError === 'otp_expired') {
      setLinkError('이미 사용했거나 만료된 링크입니다. 재설정 링크는 한 번만 쓸 수 있습니다.');
    }

    // URL에서 토큰 확인 (Supabase가 자동으로 세션 설정)
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();

      if (session) {
        setIsValidSession(true);
      } else {
        // URL에 recovery 토큰이 있는지 확인
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const type = hashParams.get('type');

        if (accessToken && type === 'recovery') {
          // Supabase가 자동으로 세션을 설정하므로 잠시 대기
          await new Promise(resolve => setTimeout(resolve, 500));
          const { data: { session: newSession } } = await supabase.auth.getSession();
          if (newSession) {
            setIsValidSession(true);
          }
        }
      }
      setCheckingSession(false);
    };

    checkSession();

    // Auth 상태 변경 리스너
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsValidSession(true);
        setCheckingSession(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!password || !confirmPassword) {
      setError('비밀번호를 입력해주세요.');
      return;
    }

    if (password.length < 6) {
      setError('비밀번호는 6자 이상이어야 합니다.');
      return;
    }

    if (password !== confirmPassword) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }

    setLoading(true);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) {
        console.error('Password update error:', updateError);
        setError(describeUpdateError(updateError));
      } else {
        setSuccess(true);
        // 3초 후 로그인 페이지로 이동
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  if (checkingSession) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <div className="flex-grow bg-white flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">확인 중...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!isValidSession && !checkingSession) {
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <div className="flex-grow bg-white flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-32 sm:pt-28">
          <div className="max-w-md w-full text-center">
            <div className="mb-4">
              <svg className="mx-auto h-12 w-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              유효하지 않은 링크입니다
            </h2>
            <p className="text-gray-600 mb-6">
              {linkError || '비밀번호 재설정 링크가 만료되었거나 유효하지 않습니다.'}<br />
              다시 비밀번호 찾기를 진행해주세요.
            </p>
            <button
              onClick={() => navigate('/forgot-password')}
              className="inline-block px-6 py-3 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
            >
              비밀번호 찾기로 이동
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <div className="flex-grow bg-white flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-32 sm:pt-28">
        <div className="max-w-md w-full">
          <div className="text-center mb-10">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              비밀번호 재설정
            </h1>
            <p className="text-sm text-gray-600">
              새로운 비밀번호를 입력해주세요
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded p-8">
            {success ? (
              <div className="text-center">
                <div className="mb-4">
                  <svg className="mx-auto h-12 w-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  비밀번호가 변경되었습니다
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  새로운 비밀번호로 로그인해주세요.<br />
                  잠시 후 로그인 페이지로 이동합니다.
                </p>
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mx-auto"></div>
              </div>
            ) : (
              <form className="space-y-5" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                    새 비밀번호
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    className="appearance-none block w-full px-3 py-2.5 border border-gray-300 rounded text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="새 비밀번호를 입력하세요 (6자 이상)"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    새 비밀번호 확인
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    required
                    className="appearance-none block w-full px-3 py-2.5 border border-gray-300 rounded text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="비밀번호를 다시 입력하세요"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </div>

                {error && (
                  <div className="rounded bg-red-50 border border-red-200 p-3">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                <div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex justify-center py-3 px-4 border border-transparent text-base font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? '변경 중...' : '비밀번호 변경'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ResetPassword;
